import { z } from "zod";

// =============================================================================
// EXPORT OPTIONS SCHEMA
// =============================================================================
// NOTE: Export options are SETTINGS-ONLY - the LLM cannot override these.
// Users configure these in Settings > Excel Export. Keeping the schema for
// internal use and response typing only.

export const ExportOptionsSchema = z.object({
    black_and_white: z.boolean().optional().default(false)
        .describe("Convert text colors to black (or white on dark backgrounds) for cleaner export"),
    collapse_groups: z.boolean().optional().default(true)
        .describe("Collapse row/column groups that are fully contained within the export range"),
    clear_comments: z.boolean().optional().default(false)
        .describe("Remove cell comments/notes from the export for cleaner output"),
});

// =============================================================================
// SUBMIT ARTIFACT PARAMS SCHEMA
// =============================================================================

const ArtifactRequestBaseSchema = z.object({
    // Location
    sheet_name: z.string()
        .describe("Name of the sheet containing the content to export"),
    // Description for citation/metadata
    description: z.string().optional()
        .describe("Human-readable description of what this artifact represents (e.g., 'Q3 2024 Revenue by Region')"),
});

export const ChartSubmitArtifactParamsSchema = ArtifactRequestBaseSchema.extend({
    artifact_type: z.literal("chart")
        .describe("Type of content to export: 'chart' for a chart object"),
    chart_name: z.string().min(1)
        .describe("Name of the chart to export (e.g., 'Chart 1', 'Revenue Chart')."),
});

export const RangeSubmitArtifactParamsSchema = ArtifactRequestBaseSchema.extend({
    artifact_type: z.literal("range")
        .describe("Type of content to export: 'range' for cell data"),
    range_ref: z.string().min(1)
        .describe("Cell range to export (e.g., 'A1:D10', 'B2:G25')."),
});

export const SubmitArtifactParamsSchema = z.discriminatedUnion("artifact_type", [
    ChartSubmitArtifactParamsSchema,
    RangeSubmitArtifactParamsSchema,
]);

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type SubmitArtifactParams = z.infer<typeof SubmitArtifactParamsSchema>;
export type ExportOptions = z.infer<typeof ExportOptionsSchema>;

// =============================================================================
// RESPONSE INTERFACES
// =============================================================================

export interface ArtifactSource {
    /** Path to the original/source Excel file */
    file: string;
    /** Sheet name where content was exported from */
    sheet: string;
    /** Chart name or range reference */
    ref: string;
}

export interface SubmitArtifactResponse {
    success: boolean;
    
    /** Unique artifact ID (artifact_xxx format) */
    artifact_id: string;
    
    /** Citation ID for metadata system (excel_xxx format) */
    excel_id: string;
    
    /** Type of artifact created */
    artifact_type: "chart" | "range";
    
    /** Source tracking info for re-sync */
    source: ArtifactSource;
    
    /** 
     * PNG image data (base64 encoded) - from Python backend.
     * NOTE: This should NOT be returned in the tool result to the LLM!
     * Images are stored in contextResolver and referenced via _imageIds.
     * This field is optional in the response to support the stripped version.
     */
    image_base64?: string;
    
    /** Image format */
    image_format: string;
    
    /** Path to mini-workbook for OLE/COM insertion */
    ole_file_path: string;
    
    /** Export options that were used */
    export_options: ExportOptions;
    
    /** Preview image (base64, same as image for now) */
    preview_base64?: string;
    
    /** Human-readable description */
    description?: string;
    
    /** Unix timestamp when created */
    created_at: number;
    
    /** Width of the artifact in points (for aspect ratio preservation) */
    width_points?: number;
    
    /** Height of the artifact in points (for aspect ratio preservation) */
    height_points?: number;
    
    /** Aspect ratio (width/height) for dimension calculation during insertion */
    aspect_ratio?: number;
    
    /** Any warnings during export */
    warnings?: string[];
    
    /** Error message if failed */
    error?: string;
    
    /**
     * Image IDs for automatic injection in next user message.
     * Convention used by extractImageIds() in messageBuilder.
     * Images are stored in contextResolver and referenced by these IDs.
     */
    _imageIds?: string[];
}



